import React, { useContext } from 'react';

import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { Dropdown, DropdownMenu, DropdownItem } from 'components/Dropdown2';
import { Flyout, FlyoutContent, FlyoutFooter, FlyoutTitle } from 'components/Flyout';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import Message from 'components/Message';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import { ICONS } from 'constants/icons';
import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenue } from 'selectors/general';
import { selectIsDesktop } from 'selectors/ui';

import { mapOrderToFlyoutData } from './SkuOrderFlyout.helpers';
import useEmailOrderCustomer from './useEmailOrderCustomer';
import sharedMessages from './SkuOrder.messages';
import messages from './SkuOrderFlyout.messages';
import { SHOW_REPORT_USER_BUTTON } from '../Orders/SkuOrders.constants';
import { LIFECYCLE_STATUS } from './SkuEnumHelpers';
import './SkuOrderFlyout.scss';

export const SKU_ORDER_FLYOUT_ID = 'sku-order-flyout';

const { block, element } = bem('sku-order-flyout');

const AGENT_MESSAGE_MAP = {
  auto: messages.lifecycleAgentAutomatic,
  customer: messages.lifecycleAgentCustomer,
  partner: messages.lifecycleAgentPartner,
  classpass: messages.lifecycleAgentClasspass,
};

const ACTION_MESSAGE_MAP = {
  'marked fulfilled': messages.lifecycleActionMarkedFulfilled,
  cancelled: messages.lifecycleActionCancelled,
};

const STATUS_CONTENT = {
  [LIFECYCLE_STATUS.CANCELLED]: { message: sharedMessages.orderCancelled },
  [LIFECYCLE_STATUS.FULFILLED]: { message: sharedMessages.fulfilled, withCheck: true, dark: true },
};

const HEADER_STATUS_CONTENT = {
  [LIFECYCLE_STATUS.CANCELLED]: { message: messages.cancelledStatus },
  [LIFECYCLE_STATUS.FULFILLED]: { message: sharedMessages.fulfilled, withCheck: true, dark: true },
};

const FLYOUT_ORDER_ITEM_SHAPE = {
  core: PropTypes.shape({
    itemId: PropTypes.string,
    itemSummary: PropTypes.shape({
      name: PropTypes.string,
      itemId: PropTypes.string,
    }),
    cancelledAt: PropTypes.string,
    cancellationType: PropTypes.string,
  }),
  payableAmount: PropTypes.shape({
    amount: PropTypes.number,
    currencyCode: PropTypes.string,
  }),
  basePayableAmount: PropTypes.shape({
    amount: PropTypes.number,
    currencyCode: PropTypes.string,
  }),
};

const FLYOUT_ORDER_SHAPE = {
  userName: PropTypes.string,
  venueName: PropTypes.string,
  lifecycleStatus: PropTypes.string,
  orderItems: PropTypes.arrayOf(PropTypes.shape(FLYOUT_ORDER_ITEM_SHAPE)),
  core: PropTypes.shape({
    orderId: PropTypes.string,
    userId: PropTypes.number,
    createdOn: PropTypes.string,
    markedFulfilledAt: PropTypes.string,
    userMarkedPickupAt: PropTypes.string,
  }),
};

const StatusPill = ({ message, withCheck = false, dark = false, header = false }) => (
  <span className={element('status-pill', { dark, header })}>
    {withCheck && <Isvg src={ICONS.checkmark} className={element('status-pill-icon')} aria-hidden="true" />}
    <Message message={message} />
  </span>
);

StatusPill.propTypes = {
  message: intlMessage.isRequired,
  withCheck: PropTypes.bool,
  dark: PropTypes.bool,
  header: PropTypes.bool,
};

const ItemActionsDots = () => (
  <span className={element('item-actions-dots')} aria-hidden="true">
    <span className={element('item-actions-dot')} />
    <span className={element('item-actions-dot')} />
    <span className={element('item-actions-dot')} />
  </span>
);

const OrderItemRow = ({ group, isOrderActive, showItemActions, onEditItem, onItemOutOfStock }) => {
  const intl = useIntl();

  return (
    <div className={element('item-row', { cancelled: group.allCancelled })}>
      <span className={element('item-quantity')}>{group.quantity} x</span>
      <span className={element('item-name')}>{group.name}</span>
      <div className={element('item-payout-group')}>
        <span className={element('item-payout-values')}>
          {group.allCancelled && group.basePayableAmountDisplay && (
            <span className={element('item-base-payout')}>{group.basePayableAmountDisplay}</span>
          )}
          <span className={element('item-payout')}>{group.payableAmountDisplay}</span>
        </span>
        {group.isOutOfStock && (
          <span className={element('item-out-of-stock')}>
            <Message message={messages.markedOutOfStock} />
          </span>
        )}
      </div>
      {isOrderActive && showItemActions && (
        <Dropdown
          className={element('item-actions')}
          contentClassName={element('item-actions-dropdown')}
          attachment="right"
          isSmallRadiusStyle
          showMenuCaret={false}>
          {({ toggleMenu, closeMenu }) => (
            <>
              <button
                type="button"
                className={element('item-actions-btn')}
                aria-label={intl.formatMessage(messages.itemActions)}
                onClick={toggleMenu}>
                <ItemActionsDots />
              </button>
              <DropdownMenu className={element('item-actions-menu')}>
                <DropdownItem
                  onClick={() => {
                    closeMenu();
                    onEditItem?.(group.itemId);
                  }}>
                  <span className={element('item-action-row')}>
                    <Isvg src={ICONS.editPencilSku} className={element('item-action-icon')} aria-hidden="true" />
                    <Message message={messages.editItem} />
                  </span>
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    closeMenu();
                    onItemOutOfStock?.(group.name, group.itemId);
                  }}>
                  <span className={element('item-action-row')}>
                    <Isvg src={ICONS.alertTriangle} className={element('item-action-icon')} aria-hidden="true" />
                    <Message message={messages.itemOutOfStock} />
                  </span>
                </DropdownItem>
              </DropdownMenu>
            </>
          )}
        </Dropdown>
      )}
    </div>
  );
};

OrderItemRow.propTypes = {
  group: PropTypes.shape({
    itemId: PropTypes.string,
    name: PropTypes.string,
    quantity: PropTypes.number,
    allCancelled: PropTypes.bool,
    isOutOfStock: PropTypes.bool,
    payableAmountDisplay: PropTypes.string,
    basePayableAmountDisplay: PropTypes.string,
  }).isRequired,
  isOrderActive: PropTypes.bool,
  showItemActions: PropTypes.bool,
  onEditItem: PropTypes.func,
  onItemOutOfStock: PropTypes.func,
};

const ActionRow = ({ icon, label, danger = false, onClick }) => (
  <button type="button" className={element('action-row', { danger })} onClick={onClick}>
    <span className={element('action-row-content')}>
      <Isvg src={icon} className={element('action-row-icon', { danger })} aria-hidden="true" />
      <span className={element('action-row-label')}>
        <Message message={label} />
      </span>
    </span>
    <Isvg src={ICONS.chevronRightThin} className={element('action-row-chevron')} aria-hidden="true" />
  </button>
);

ActionRow.propTypes = {
  icon: PropTypes.string.isRequired,
  label: intlMessage.isRequired,
  danger: PropTypes.bool,
  onClick: PropTypes.func,
};

const LifecycleDescription = ({ descriptions, intl, inFooter = false }) => {
  if (!descriptions?.length) return null;

  return (
    <div className={element('lifecycle-descriptions', { footer: inFooter })}>
      {descriptions.map(desc => {
        const actionMsg = ACTION_MESSAGE_MAP[desc.action];
        const agentMsg = AGENT_MESSAGE_MAP[desc.agentKey];
        const actionLabel = actionMsg ? intl.formatMessage(actionMsg) : desc.action;
        const agentLabel = agentMsg ? intl.formatMessage(agentMsg) : desc.agentKey;
        const descriptionKey = `${desc.action}|${desc.agentKey}|${desc.timeDisplay}|${desc.dateDisplay}`;
        return (
          <p key={descriptionKey} className={element('lifecycle-description')}>
            <Message
              message={messages.lifecycleDescription}
              values={{
                action: actionLabel,
                agent: agentLabel,
                date: desc.dateDisplay,
                time: desc.timeDisplay,
              }}
            />
          </p>
        );
      })}
    </div>
  );
};

LifecycleDescription.propTypes = {
  descriptions: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.string,
      agentKey: PropTypes.string,
      dateDisplay: PropTypes.string,
      timeDisplay: PropTypes.string,
    })
  ),
  intl: PropTypes.shape({ formatMessage: PropTypes.func.isRequired }).isRequired,
  inFooter: PropTypes.bool,
};

const SkuOrderFlyout = ({
  order,
  onFulfill,
  isFulfilling = false,
  onCancelOrder,
  onItemOutOfStock,
  onCustomerMissedPickup,
  onEditItem,
  flyoutId = SKU_ORDER_FLYOUT_ID,
  isInTodayTab = false,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { closeFlyout } = useContext(FlyoutContext);
  const isDesktop = useSelector(selectIsDesktop);
  const { venueId, timezone: venueTimezone } = useSelector(selectActiveVenue);
  const { openEmailCustomerModal } = useEmailOrderCustomer();

  const handleEditItem = itemId => {
    if (!venueId || !itemId) return;
    closeFlyout();
    navigate(`${makePath.items(venueId)}?editItem=${itemId}`);
  };

  const flyoutData = order ? mapOrderToFlyoutData(order, { intl, venueTimezone, locale: intl.locale }) : null;

  const handleContactCustomer = () => {
    openEmailCustomerModal({
      orderCreatedOn: order?.core?.createdOn,
      venueName: order?.venueName,
      userId: order?.core?.userId,
      userName: flyoutData?.memberName,
    });
  };

  const isActive = flyoutData?.lifecycleStatus === LIFECYCLE_STATUS.ACTIVE;
  const isFulfilled = flyoutData?.lifecycleStatus === LIFECYCLE_STATUS.FULFILLED;
  const showCustomerMissedPickup = (isActive || isFulfilled) && isInTodayTab;
  const showItemActions = Boolean(onItemOutOfStock);
  const showOrderActions = Boolean(onCancelOrder || onItemOutOfStock);
  const showActionSection = (isActive && showOrderActions) || showCustomerMissedPickup;
  const statusContent = STATUS_CONTENT[flyoutData?.lifecycleStatus];
  const headerStatusContent = HEADER_STATUS_CONTENT[flyoutData?.lifecycleStatus];
  const isCancelled = flyoutData?.lifecycleStatus === LIFECYCLE_STATUS.CANCELLED;

  return (
    <Flyout id={flyoutId} className="sku-order-flyout-shell" showBackButton={!isDesktop}>
      {flyoutData && (
        <>
          <FlyoutTitle>
            <span className={element('title')}>
              <span className={element('title-main')}>
                <Message message={messages.titlePrefix} />{' '}
                <span className={element('title-id')}>{flyoutData.orderId}</span>
              </span>
              {headerStatusContent && <StatusPill {...headerStatusContent} header />}
            </span>
          </FlyoutTitle>

          <FlyoutContent>
            <div className={block()}>
              {/* Customer section */}
              <section className={element('section')}>
                <h3 className={element('section-heading')}>
                  <Message message={messages.sectionCustomer} />
                </h3>
                <div className={element('customer-row')}>
                  <div className={element('customer-identity')}>
                    <span className={element('customer-avatar')} aria-hidden="true">
                      {flyoutData.memberInitial || '?'}
                    </span>
                    <span className={element('customer-name')}>{flyoutData.memberName || '—'}</span>
                  </div>
                  <div className={element('customer-actions')}>
                    {isActive && isInTodayTab && (
                      <Button styled="secondary" className={element('customer-btn')} onClick={handleContactCustomer}>
                        <Isvg src={ICONS.envelope} className={element('customer-btn-icon')} aria-hidden="true" />
                        <Message message={messages.contactCustomer} />
                      </Button>
                    )}
                    {SHOW_REPORT_USER_BUTTON && (
                      <Button styled="secondary" className={element('customer-btn')}>
                        <Isvg src={ICONS.flag} className={element('customer-btn-icon')} aria-hidden="true" />
                        <Message message={messages.reportUser} />
                      </Button>
                    )}
                  </div>
                </div>
              </section>

              {/* Order details section */}
              <section className={element('section')}>
                <div className={element('section-heading-row')}>
                  <h3 className={element('section-heading')}>
                    <Message message={messages.sectionOrderDetails} />
                  </h3>
                  {isCancelled && (
                    <span className={element('order-cancelled-chip')}>
                      <Message message={sharedMessages.orderCancelled} />
                    </span>
                  )}
                </div>
                <div className={element('items-list')}>
                  {flyoutData.itemGroups.map(group => (
                    <OrderItemRow
                      key={group.itemId}
                      group={group}
                      isOrderActive={isActive}
                      showItemActions={showItemActions}
                      onEditItem={onEditItem ?? handleEditItem}
                      onItemOutOfStock={onItemOutOfStock}
                    />
                  ))}
                  <div className={element('total-row')}>
                    <span className={element('total-label')}>
                      <Message message={messages.totalPayout} />
                      <TooltipToggle name="sku-order-flyout-total-payout" className={element('total-payout-icon')}>
                        <Isvg src={ICONS.infoGreyLight} />
                      </TooltipToggle>
                      <TooltipContent name="sku-order-flyout-total-payout" place="top">
                        <Message message={sharedMessages.payableAmountTooltip} />
                      </TooltipContent>
                    </span>
                    <span className={element('total-amount')}>{flyoutData.totalPayableAmountDisplay || '—'}</span>
                  </div>
                </div>
              </section>

              {showActionSection && (
                <section className={element('actions-section')}>
                  {isActive && onItemOutOfStock && (
                    <ActionRow
                      icon={ICONS.alertTriangle}
                      label={messages.itemOutOfStock}
                      onClick={() => onItemOutOfStock(undefined, undefined)}
                    />
                  )}
                  {showCustomerMissedPickup && (
                    <ActionRow
                      icon={ICONS.userMissedPickup}
                      label={messages.customerMissedPickup}
                      onClick={onCustomerMissedPickup}
                    />
                  )}
                  {isActive && onCancelOrder && (
                    <ActionRow icon={ICONS.cross} label={messages.cancelOrder} danger onClick={onCancelOrder} />
                  )}
                </section>
              )}
            </div>
          </FlyoutContent>

          {/* Footer */}
          {(isActive || statusContent) && (
            <FlyoutFooter>
              <div className={element('footer')}>
                {isActive && onFulfill && (
                  <Button
                    styled="primary"
                    className={element('fulfill-btn')}
                    onClick={onFulfill}
                    disabled={isFulfilling}>
                    <Message message={messages.markFulfilled} />
                  </Button>
                )}
                {isActive && !isDesktop && (
                  <Button styled="secondary" className={element('close-btn')} onClick={closeFlyout}>
                    <Message message={messages.close} />
                  </Button>
                )}
                {!isActive && statusContent && (
                  <>
                    <StatusPill {...statusContent} />
                    <LifecycleDescription descriptions={flyoutData.lifecycleStatusDescriptions} intl={intl} inFooter />
                  </>
                )}
              </div>
            </FlyoutFooter>
          )}
        </>
      )}
    </Flyout>
  );
};

SkuOrderFlyout.propTypes = {
  order: PropTypes.shape(FLYOUT_ORDER_SHAPE),
  onFulfill: PropTypes.func,
  isFulfilling: PropTypes.bool,
  onCancelOrder: PropTypes.func,
  onItemOutOfStock: PropTypes.func,
  onCustomerMissedPickup: PropTypes.func,
  onEditItem: PropTypes.func,
  flyoutId: PropTypes.string,
  isInTodayTab: PropTypes.bool,
};

export default SkuOrderFlyout;
